import { useQuery } from '@apollo/client';
import { useContext, useMemo } from 'react';
import { IApiTenderRequest, IApiTenderBidResponse } from '../Tender/types';
import { GET_BID_ORIGIN_ID, GET_UPD_TENDER_NAV, GET_TENDER_NAV } from './queries';
import { FeatureFlag, useFeatureFlag } from 'src/helpers/featureFlag';
import { useLocation } from 'react-router-dom';
import { Paths } from 'src/pages/paths';
import { BidItemType } from '@tendium/prom-types/tender';
import { GET_TENDER_BID } from '../Tender/queries';
import { TenderNavContextData, TenderNavResponseData } from './types';
import TenderNavData from '.';
import { TenderNavContext } from './context';

interface BidOriginIdResponse {
  readonly getBidV2: {
    id: string;
    itemId: string;
    itemType: BidItemType;
  } | null;
}
interface BidOriginIdRequest {
  bidId: string;
}
type TenderNavResponse =
  | { readonly getUpdatedTender: TenderNavResponseData | null }
  | { readonly getTender: TenderNavResponseData | null };
type TenderNavRequest = { id: string } | { id: string; bidId: string };

export function useTenderNavData(): TenderNavContextData {
  const isEditableTender = useFeatureFlag(FeatureFlag.EditableDetailspage);

  const { pathname } = useLocation();
  const [bidPathName] = Paths.BIDDING_PAGE.split('/');
  const [, tenderPathName] = Paths.TENDER_ROUTE.split('/');
  const [, routeSlug, pageSlug, idSlug] = pathname.split('/');
  const isBidding = bidPathName === pageSlug;
  const isTender = tenderPathName === routeSlug && bidPathName !== pageSlug;

  const { data: bidOriginData } = useQuery<BidOriginIdResponse, BidOriginIdRequest>(GET_BID_ORIGIN_ID, {
    variables: {
      bidId: idSlug ?? ''
    },
    skip: !isBidding && isTender && !idSlug
  });
  const tenderId = useMemo(
    () => (isTender ? pageSlug : bidOriginData?.getBidV2?.itemId ?? undefined),
    [bidOriginData, isTender, pageSlug]
  );

  const { data: tenderBidData } = useQuery<IApiTenderBidResponse, IApiTenderRequest>(GET_TENDER_BID, {
    variables: {
      id: tenderId ?? ''
    },
    skip: isBidding || !isEditableTender || !tenderId
  });
  const bidId = useMemo(
    () =>
      isBidding ? idSlug : !!tenderBidData?.getTender.bids.length ? tenderBidData.getTender.bids[0].id : undefined,
    [idSlug, isBidding, tenderBidData]
  );

  const { data: tenderData, loading } = useQuery<TenderNavResponse, TenderNavRequest>(
    isEditableTender && bidId ? GET_UPD_TENDER_NAV : GET_TENDER_NAV,
    {
      variables: isEditableTender && bidId ? { id: tenderId ?? '', bidId: bidId ?? '' } : { id: tenderId ?? '' },
      skip: isTender ? !tenderId : !tenderId || !bidId
    }
  );

  const data = useMemo(
    () =>
      (tenderData as { readonly getTender: TenderNavResponseData | null })?.getTender ??
      (tenderData as { readonly getUpdatedTender: TenderNavResponseData | null })?.getUpdatedTender ??
      undefined,
    [tenderData]
  );

  return useMemo(
    () => ({ data: data ? new TenderNavData(data, isBidding, !!isEditableTender && !!bidId) : null, loading }),
    [data, isBidding, isEditableTender, bidId, loading]
  );
}

export function useTenderNav(): TenderNavContextData {
  return useContext(TenderNavContext);
}
